import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { Link, router } from '@inertiajs/react';
import { Search } from 'lucide-react';
import { useState } from 'react';

interface ArticleResult {
    id: number;
    title: string;
    slug: string;
    excerpt?: string | null;
    kicker?: string | null;
    main_image?: string | null;
    published_at?: string | null;
    category?: { slug: string; name: string } | null;
    author_name?: string | null;
}

interface AgentResult {
    id: number;
    slug: string;
    name: string;
    role?: string | null;
    main_image?: string | null;
}

interface InfluencerResult {
    id: number;
    slug: string;
    name: string;
    specialty?: string | null;
    instagram_handle?: string | null;
    main_image?: string | null;
}

interface TeamMemberResult {
    id: number;
    slug: string;
    name: string;
    position?: string | null;
    main_image?: string | null;
}

interface HotelResult {
    id: number;
    slug: string;
    name: string;
    city_slug?: string | null;
    city_display?: string | null;
    editorial_lede?: string | null;
}

interface PageResult {
    id: number | string;
    title: string;
    slug: string;
    subtitle?: string | null;
    image_url?: string | null;
}

interface CategoryResult {
    id: number;
    slug: string;
    name: string;
}

interface SearchPageProps {
    q: string;
    articles: ArticleResult[];
    agents: AgentResult[];
    influencers: InfluencerResult[];
    team_members: TeamMemberResult[];
    hotels: HotelResult[];
    pages: PageResult[];
    categories: CategoryResult[];
}

export default function SearchPage({ q, articles, agents, influencers, team_members, hotels, pages, categories }: SearchPageProps) {
    const [value, setValue] = useState(q);

    const totalResults = articles.length + agents.length + influencers.length + team_members.length + hotels.length + pages.length + categories.length;
    const hasQuery = q.trim() !== '';
    const qLower = q.trim().toLowerCase();
    const agentNameMatch = qLower !== '' && agents.some((a) => a.name.toLowerCase().includes(qLower));
    const teamNameMatch = qLower !== '' && team_members.some((m) => m.name.toLowerCase().includes(qLower));
    // Promote Pages to top when the query is a substring of a page/nav title —
    // mirrors agentNameMatch. "Solid" intent signal: someone typing "contact"
    // or "become an agent" almost always wants the page itself, not articles
    // mentioning those topics.
    const pageTitleMatch = qLower !== '' && pages.some((p) => p.title.toLowerCase().includes(qLower));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = value.trim();
        router.visit(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
    };

    return (
        <PublicLayout>
            <MetaHead title={hasQuery ? `Search: ${q}` : 'Search'} description="Search LuxRally Travel." />

            {/* Hero search section */}
            <section className="nav-px bg-[#0b2222] pt-32 pb-16 md:pt-40 md:pb-24">
                <div className="mx-auto max-w-3xl">
                    <p className="lux-caps lux-caps-sm mb-8 text-center tracking-[0.3em] text-[#F5F5F5]/50">Search</p>
                    <form onSubmit={handleSubmit}>
                        <div className="flex items-center gap-4 border-b border-[#F5F5F5]/25 pb-4 transition-colors focus-within:border-[#F5F5F5]/60">
                            <Search className="h-5 w-5 flex-shrink-0 text-[#F5F5F5]/45" strokeWidth={1.5} />
                            <input
                                type="text"
                                name="q"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="Journal, advisors, places…"
                                aria-label="Search"
                                className="lux-didot lux-didot-md flex-1 bg-transparent text-[#F5F5F5] placeholder:text-[#F5F5F5]/30 focus:outline-none"
                                autoFocus
                            />
                        </div>
                    </form>
                    {hasQuery && totalResults > 0 && (
                        <p className="lux-caps lux-caps-sm mt-8 text-center tracking-[0.25em] text-[#F5F5F5]/40">
                            {totalResults} result{totalResults === 1 ? '' : 's'}
                        </p>
                    )}
                </div>
            </section>

            <div className="nav-px bg-[#0b2222] pb-24 text-[#F5F5F5]">
                <div className="mx-auto max-w-6xl">
                    {!hasQuery && (
                        <div className="py-24 text-center">
                            <p className="lux-didot lux-didot-lg text-[#F5F5F5]">What can we help you find?</p>
                            <p className="lux-body lux-body-sm mt-5 text-[#F5F5F5]/55">Journal stories, advisors, destinations, and more.</p>
                        </div>
                    )}

                    {hasQuery && totalResults === 0 && (
                        <div className="py-24 text-center">
                            <p className="lux-didot lux-didot-lg text-[#F5F5F5]">Nothing found for &ldquo;{q}&rdquo;.</p>
                            <p className="lux-body lux-body-sm mt-5 text-[#F5F5F5]/55">Try a shorter or more general term.</p>
                        </div>
                    )}

                    {hasQuery && totalResults > 0 && (
                        <div className="flex flex-col gap-20">
                            {pageTitleMatch && pages.length > 0 && <PagesSection pages={pages} />}
                            {teamNameMatch && team_members.length > 0 && <TeamSection members={team_members} />}
                            {agentNameMatch && agents.length > 0 && <AgentsSection agents={agents} />}
                            {articles.length > 0 && <ArticlesSection articles={articles} />}
                            {!agentNameMatch && agents.length > 0 && <AgentsSection agents={agents} />}
                            {!teamNameMatch && team_members.length > 0 && <TeamSection members={team_members} />}
                            {influencers.length > 0 && <InfluencersSection influencers={influencers} />}
                            {hotels.length > 0 && <HotelsSection hotels={hotels} />}
                            {!pageTitleMatch && pages.length > 0 && <PagesSection pages={pages} />}
                            {categories.length > 0 && <CategoriesSection categories={categories} />}
                        </div>
                    )}
                </div>
            </div>
        </PublicLayout>
    );
}

function SectionHeader({ label, count }: { label: string; count: number }) {
    return (
        <div className="mb-8 flex items-baseline justify-between border-b border-[#F5F5F5]/15 pb-4">
            <p className="lux-caps lux-caps-sm tracking-[0.3em] text-[#F5F5F5]/55">{label}</p>
            <span className="lux-caps text-[11px] tracking-[0.25em] text-[#F5F5F5]/35">
                {count} result{count === 1 ? '' : 's'}
            </span>
        </div>
    );
}

function ArticlesSection({ articles }: { articles: ArticleResult[] }) {
    return (
        <section>
            <SectionHeader label="Journal" count={articles.length} />
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                {articles.map((a) => (
                    <Link key={a.id} href={`/journal/${a.slug}`} className="group block">
                        {a.main_image && (
                            <div className="mb-5 aspect-[4/3] w-full overflow-hidden bg-[#F5F5F5]/5">
                                <img
                                    src={a.main_image}
                                    alt={a.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                                />
                            </div>
                        )}
                        {a.kicker && <p className="lux-caps lux-caps-sm mb-2 tracking-[0.25em] text-[#F5F5F5]/50">{a.kicker}</p>}
                        <h3 className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{a.title}</h3>
                        {a.excerpt && <p className="lux-body lux-body-sm mt-3 line-clamp-2 text-[#F5F5F5]/65">{a.excerpt}</p>}
                        {a.author_name && <p className="lux-body lux-body-sm mt-4 text-[#F5F5F5]/45 italic">By {a.author_name}</p>}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function AgentsSection({ agents }: { agents: AgentResult[] }) {
    return (
        <section>
            <SectionHeader label="Advisors" count={agents.length} />
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-4">
                {agents.map((a) => (
                    <Link key={a.id} href={`/the-team/${a.slug}`} className="group block text-center">
                        {a.main_image ? (
                            <div className="mb-4 aspect-square w-full overflow-hidden rounded-full bg-[#F5F5F5]/5">
                                <img
                                    src={a.main_image}
                                    alt={a.name}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                        ) : (
                            <div className="mb-4 aspect-square w-full rounded-full bg-[#F5F5F5]/5" aria-hidden />
                        )}
                        <p className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{a.name}</p>
                        {a.role && <p className="lux-caps lux-caps-lg mt-2 tracking-[0.15em]! text-[#F5F5F5]/55">{a.role}</p>}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function TeamSection({ members }: { members: TeamMemberResult[] }) {
    return (
        <section>
            <SectionHeader label="Team" count={members.length} />
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-4">
                {members.map((m) => (
                    <Link key={m.id} href={`/team/${m.slug}`} className="group block text-center">
                        {m.main_image ? (
                            <div className="mb-4 aspect-square w-full overflow-hidden rounded-full bg-[#F5F5F5]/5">
                                <img
                                    src={m.main_image}
                                    alt={m.name}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                        ) : (
                            <div className="mb-4 aspect-square w-full rounded-full bg-[#F5F5F5]/5" aria-hidden />
                        )}
                        <p className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{m.name}</p>
                        {m.position && <p className="lux-caps lux-caps-lg mt-2 tracking-[0.15em]! text-[#F5F5F5]/55">{m.position}</p>}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function InfluencersSection({ influencers }: { influencers: InfluencerResult[] }) {
    return (
        <section>
            <SectionHeader label="Influencers" count={influencers.length} />
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-4">
                {influencers.map((i) => (
                    <Link key={i.id} href={`/${i.slug}`} className="group block text-center">
                        {i.main_image ? (
                            <div className="mb-4 aspect-square w-full overflow-hidden rounded-full bg-[#F5F5F5]/5">
                                <img
                                    src={i.main_image}
                                    alt={i.name}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                        ) : (
                            <div className="mb-4 aspect-square w-full rounded-full bg-[#F5F5F5]/5" aria-hidden />
                        )}
                        <p className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{i.name}</p>
                        {i.specialty && <p className="lux-caps lux-caps-lg mt-2 tracking-[0.15em]! text-[#F5F5F5]/55">{i.specialty}</p>}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function HotelsSection({ hotels }: { hotels: HotelResult[] }) {
    return (
        <section>
            <SectionHeader label="Hotels" count={hotels.length} />
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                {hotels.map((h) => (
                    <Link
                        key={h.id}
                        href={h.city_slug ? `/hotels/${h.city_slug}/${h.slug}` : `/hotels/${h.slug}`}
                        className="group block"
                    >
                        {h.city_display && <p className="lux-caps lux-caps-sm mb-2 tracking-[0.25em] text-[#F5F5F5]/50">{h.city_display}</p>}
                        <h3 className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{h.name}</h3>
                        {h.editorial_lede && (
                            <p className="lux-body lux-body-sm mt-3 line-clamp-2 text-[#F5F5F5]/65">{h.editorial_lede}</p>
                        )}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function PagesSection({ pages }: { pages: PageResult[] }) {
    return (
        <section>
            <SectionHeader label="Pages" count={pages.length} />
            <div className="flex flex-col gap-3">
                {pages.map((p) => (
                    <Link
                        key={p.id}
                        href={`/${p.slug}`}
                        className="group flex items-center gap-6 border-l border-[#F5F5F5]/20 py-3 pr-6 pl-3 transition-colors hover:border-[#F5F5F5] hover:bg-[#F5F5F5]/[0.03] sm:gap-8"
                    >
                        {p.image_url ? (
                            <div className="aspect-[3/2] w-40 flex-none overflow-hidden bg-[#F5F5F5]/5 sm:w-56">
                                <img
                                    src={p.image_url}
                                    alt=""
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                />
                            </div>
                        ) : (
                            <div className="aspect-[3/2] w-40 flex-none bg-[#F5F5F5]/[0.04] sm:w-56" aria-hidden />
                        )}
                        <div className="min-w-0 flex-1 py-2">
                            <h3 className="lux-didot lux-didot-md text-[#F5F5F5] transition-opacity group-hover:opacity-80">{p.title}</h3>
                            {p.subtitle && (
                                <p className="lux-body lux-body-sm mt-2 text-[#F5F5F5]/65">{p.subtitle}</p>
                            )}
                        </div>
                        <span
                            aria-hidden
                            className="lux-didot lux-didot-md flex-none text-[#F5F5F5]/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#F5F5F5]"
                        >
                            →
                        </span>
                    </Link>
                ))}
            </div>
        </section>
    );
}

function CategoriesSection({ categories }: { categories: CategoryResult[] }) {
    return (
        <section>
            <SectionHeader label="Categories" count={categories.length} />
            <div className="flex flex-wrap gap-3">
                {categories.map((c) => (
                    <Link
                        key={c.id}
                        href={`/journal/category/${c.slug}`}
                        className="lux-caps lux-caps-sm rounded-sm border border-[#F5F5F5]/25 px-5 py-2.5 tracking-[0.25em] text-[#F5F5F5]/80 transition-colors hover:border-[#F5F5F5] hover:bg-[#F5F5F5] hover:text-[#0b2222]"
                    >
                        {c.name}
                    </Link>
                ))}
            </div>
        </section>
    );
}
